'use client';

import type { HomepageRail, RailProduct } from './homepage-products';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore, type GlobalState } from '@eliadmin/core/src/stores/global-store';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import { memo, useRef } from 'react';
import { HomepageSection } from './ui/homepage-section';
import { SectionHeading } from './ui/section-heading';

type ProductRailLabels = {
  badge: string;
  previous: string;
  next: string;
  viewProduct: string;
  priceOnRequest: string;
};

type ProductRailProps = {
  id?: string;
  rail: HomepageRail;
  locale: string;
  title: string;
  subtitle: string;
  products: RailProduct[];
  labels: ProductRailLabels;
  showExcerpt?: boolean;
  className?: string;
};

const navButtonClassName =
  'h-11 w-11 cursor-pointer rounded-full border-[1.5px] border-[#e6eaee] bg-white text-[1.1rem] transition-all duration-200 hover:border-[#14181c] hover:bg-[#14181c] hover:text-white';

export function ProductRail({
  id,
  rail,
  locale,
  title,
  subtitle,
  products,
  labels,
  showExcerpt = false,
  className,
}: ProductRailProps) {
  const railRef = useRef<HTMLDivElement>(null);
  const me = useCustomerStore(state => state.me);
  const isInitialized = useCustomerStore(state => state.isInitialized);
  const authToken = me?.authToken;
  // Two rails subscribe, not three broad subscriptions on every one of the 18 cards.
  const currency = useGlobalStore(state => state.generalLayout.currency);
  const currencySettings = useGlobalStore(state => state.generalLayout.currency_settings);
  const priceWithoutVatFirst = useGlobalStore(
    state => state.templateSettings?.product_detail?.price_without_vat_first?.enabled,
  );

  // Logged-in customers can have their own prices, so the public server snapshot is refreshed.
  const { data: visibleProducts = products } = useQuery<RailProduct[]>({
    queryKey: [CacheKey.PRODUCT_SEGMENT_PRODUCTS, 'homepage-rail', rail, locale, me?.customer.id],
    queryFn: async () => {
      const { fetchHomepageRailProducts } = await import('./homepage-products');
      const personalized = await fetchHomepageRailProducts({ rail, locale, authToken });
      return personalized.length ? personalized : products;
    },
    enabled: isInitialized && !!authToken,
    placeholderData: products,
    staleTime: 0,
  });

  if (!visibleProducts.length) return null;

  const scroll = (direction: -1 | 1) => {
    const element = railRef.current;
    element?.scrollBy({ left: direction * element.clientWidth * 0.8, behavior: 'smooth' });
  };

  return (
    <HomepageSection id={id} className={className}>
      <SectionHeading
        title={title}
        description={subtitle}
        action={
          <div className="hidden gap-2 min-[641px]:flex">
            <button
              type="button"
              aria-label={labels.previous}
              onClick={() => scroll(-1)}
              className={navButtonClassName}
            >
              ←
            </button>
            <button
              type="button"
              aria-label={labels.next}
              onClick={() => scroll(1)}
              className={navButtonClassName}
            >
              →
            </button>
          </div>
        }
      />
      <div
        ref={railRef}
        className="grid snap-x snap-mandatory auto-cols-[72%] grid-flow-col gap-4 overflow-x-auto pb-2 [scrollbar-width:none] min-[641px]:auto-cols-[calc((100%-16px)/2.3)] min-[1025px]:auto-cols-[calc((100%-48px)/4)] [&::-webkit-scrollbar]:hidden"
      >
        {visibleProducts.map(product => (
          <ProductRailCard
            key={product.id}
            product={product}
            labels={labels}
            showExcerpt={showExcerpt}
            currency={currency}
            currencySettings={currencySettings}
            isWholesaler={me?.customer.is_wholesaler}
            priceWithoutVatFirst={priceWithoutVatFirst}
          />
        ))}
      </div>
    </HomepageSection>
  );
}

const ProductRailCard = memo(function ProductRailCard({
  product,
  labels,
  showExcerpt,
  currency,
  currencySettings,
  isWholesaler,
  priceWithoutVatFirst,
}: {
  product: RailProduct;
  labels: ProductRailLabels;
  showExcerpt: boolean;
  currency: GlobalState['generalLayout']['currency'];
  currencySettings: GlobalState['generalLayout']['currency_settings'];
  isWholesaler: boolean | undefined;
  priceWithoutVatFirst: boolean | undefined;
}) {
  const { t } = useTranslations();
  const priceDisplay = getProductPriceDisplay(product, {
    isWholesaler,
    priceWithoutVatFirst,
  });
  const isDiscounted = !priceDisplay.priceOnRequest && priceDisplay.showPreviousPrice;
  const href = `/${product.slug}`;
  const excerpt = showExcerpt ? product.excerpt : '';

  return (
    <article className="group flex snap-start flex-col rounded-[20px] border border-[#e6eaee] bg-white px-2.5 pb-[18px] pt-2.5 transition-shadow [transition-duration:250ms] hover:shadow-[0_20px_40px_-18px_rgba(0,0,0,.18)]">
      <Link
        href={href}
        prefetch={false}
        className="relative mb-3.5 flex aspect-square items-center justify-center overflow-hidden rounded-[14px] bg-[#f3f5f7] p-[22px]"
      >
        <span className="absolute left-2.5 top-2.5 z-[1] rounded-full bg-white px-2.5 py-[5px] text-[.7rem] font-bold shadow-[0_2px_8px_rgba(0,0,0,.08)]">
          {labels.badge}
        </span>
        {isDiscounted && (
          <span className="absolute right-2.5 top-2.5 z-[1] rounded-full bg-[#B80900] px-2.5 py-[5px] text-[.7rem] font-bold text-white shadow-[0_2px_8px_rgba(0,0,0,.08)]">
            {t('text24')} -{priceDisplay.discountLabel}%
          </span>
        )}
        <Image
          src={product.imageUrl ?? '/images/placeholder.png'}
          alt={product.name}
          width={500}
          height={500}
          sizes="(min-width: 1280px) 234px, (min-width: 1025px) calc(25vw - 86px), (min-width: 641px) calc(43.4783vw - 86.8696px), calc(72vw - 89.04px)"
          quality={75}
          className="max-h-full max-w-full object-contain mix-blend-multiply transition-transform [transition-duration:400ms] group-hover:scale-[1.06]"
        />
      </Link>
      <h3 className="mx-2 mb-1.5 mt-0 text-[.98rem] font-semibold leading-[1.35]">
        <Link href={href} prefetch={false}>
          {product.name}
        </Link>
      </h3>
      {excerpt && (
        <p className="mx-2 mb-2.5 mt-0 text-[.83rem] leading-[1.5] text-[#5f6b76]">{excerpt}</p>
      )}
      <div className="mx-2 mt-auto flex items-center justify-between pt-2">
        <div className="flex flex-col">
          {isDiscounted && (
            <span className="whitespace-nowrap text-[.83rem] leading-tight text-[#5f6b76] line-through">
              {formatPrice(priceDisplay.beforeDiscount, currency, currencySettings)}
            </span>
          )}
          <span
            className={cn(
              'whitespace-nowrap text-[1.25rem] font-extrabold tracking-[-0.01em]',
              isDiscounted && 'text-[#B80900]',
            )}
          >
            {priceDisplay.priceOnRequest
              ? labels.priceOnRequest
              : formatPrice(priceDisplay.current, currency, currencySettings)}
          </span>
        </div>
        <Link
          href={href}
          prefetch={false}
          aria-label={`${labels.viewProduct}: ${product.name}`}
          className={cn(
            'flex h-[42px] w-[42px] items-center justify-center rounded-full bg-[#00a1e6] font-bold text-white',
            'transition-[transform,background-color] duration-200 hover:-rotate-45 hover:bg-[#14181c]',
          )}
        >
          →
        </Link>
      </div>
    </article>
  );
});
